import React, { useState, useEffect, useCallback } from 'react';
import {
    X, Users, CheckCircle, Clock, TrendingUp, TrendingDown,
    Download, ChevronRight, Lightbulb, AlertTriangle,
    ArrowUpRight, BarChart3, Minus,
    Maximize2, Minimize2
} from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import useDrawerResize from './Analytics/useDrawerResize';
import AnalyticsAIPanel from './Analytics/AnalyticsAIPanel';
import ActivityReminderSection from './Analytics/ActivityReminderSection';

// ═══════════════════════════════════════════════════════════
//  TOKENS — mirrors ActivityCentreOverview.jsx tokens
// ═══════════════════════════════════════════════════════════
const TOKENS = {
    textPrimary: '#202938',
    textSecondary: '#626B7A',
    textMuted: '#8A8F98',
    accent: '#E86F4A',
    accentHover: '#EE9072',
    accentBg: '#FEF2EE',
    border: '#E5E1DD',
    borderLight: '#EDEBE9',
    bgPage: '#FAFAF9',
    bgCard: '#FFFFFF',
    bgHover: '#F2EFED',
    warning: '#F59E0B',
    warningBg: '#FEF3C7',
    warningText: '#92400E',
    navy: '#2A3F54',
    navyLight: '#3A5068',
    success: '#16A34A',
    successBg: '#F0FDF4',
    successText: '#166534',
    cardRadius: '6px',
};

const TYPE_LABELS = {
    task: 'Task',
    course: 'Course',
    notice: 'Notice',
    survey: 'Survey',
    compliance: 'Compliance',
    overview: 'All Activity',
};

// ═══════════════════════════════════════════════════════════
//  ANALYTICS DRAWER COMPONENT
// ═══════════════════════════════════════════════════════════
const AnalyticsDrawer = ({ isOpen, onClose, context }) => {
    // context = { type, id?, title? }
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    // ── Resizable drawer ──
    const { drawerWidth, isDragging, dragHandleProps, dragHandleElement, isFullscreen, toggleFullscreen } = useDrawerResize({
        minWidth: 400, maxWidth: 800, defaultWidth: 600,
        storageKey: 'simpleAnalyticsDrawerWidth',
    });

    const fetchData = useCallback(async () => {
        if (!context) return;
        setLoading(true);
        try {
            let url;
            if (context.type === 'overview') {
                url = '/activity-centre/analytics/overview';
            } else if (context.id) {
                url = `/activity-centre/analytics/${context.type}/${context.id}`;
            } else {
                url = `/activity-centre/analytics/${context.type}`;
            }
            const response = await api.get(url);
            setData(response.data.data);
        } catch (error) {
            console.error('Error fetching analytics:', error);
            toast.error('Failed to load analytics');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [context]);

    useEffect(() => {
        if (isOpen && context) {
            fetchData();
        }
        if (!isOpen) {
            setData(null);
            setLoading(true);
        }
    }, [isOpen, context, fetchData]);

    // Esc key handler
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen) onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    // Prevent body scroll when open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    const handleExport = async () => {
        if (!context) return;
        setExporting(true);
        try {
            const response = await api.post('/activity-centre/analytics/export', {
                type: context.type,
                id: context.id,
            }, { responseType: 'blob' });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.download = `${(context.title || context.type).replace(/[^a-z0-9]/gi, '_')}_analytics.csv`;
            link.click();
            window.URL.revokeObjectURL(url);
            toast.success('Export downloaded');
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Failed to export data');
        } finally {
            setExporting(false);
        }
    };

    const formatTime = (minutes) => {
        if (!minutes) return '—';
        if (minutes < 1) return '< 1m';
        if (minutes < 60) return `${Math.round(minutes)}m`;
        const hours = Math.floor(minutes / 60);
        const mins = Math.round(minutes % 60);
        return `${hours}h ${mins}m`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
        });
    };

    const drawerTitle = context?.title ||
        (context?.type === 'overview' ? 'All Activity' : `${TYPE_LABELS[context?.type] || ''} Analytics`);

    return (
        <>
            {/* Overlay */}
            <div
                onClick={onClose}
                style={{
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    zIndex: 999,
                    opacity: isOpen ? 1 : 0,
                    pointerEvents: isOpen ? 'auto' : 'none',
                    transition: 'opacity 300ms ease-out',
                }}
            />

            {/* Drawer */}
            <div style={{
                position: 'fixed',
                top: 0,
                right: 0,
                bottom: 0,
                width: isFullscreen ? '100vw' : `${drawerWidth}px`,
                maxWidth: '100vw',
                backgroundColor: TOKENS.bgCard,
                zIndex: 1000,
                transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
                transition: isDragging ? 'none' : 'transform 300ms ease-out, width 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '-8px 0 30px rgba(0,0,0,0.12)',
            }}>
                {/* ── Drag handle (left edge) — hidden in fullscreen ── */}
                {!isFullscreen && (
                    <div {...dragHandleProps}>
                        <div {...dragHandleElement} />
                    </div>
                )}
                {/* ── HEADER ─────────────────────────── */}
                <div style={{
                    background: `linear-gradient(135deg, ${TOKENS.navy}, ${TOKENS.navyLight})`,
                    padding: '20px 24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                        <div style={{
                            width: '40px', height: '40px',
                            backgroundColor: 'rgba(255,255,255,0.15)',
                            borderRadius: '10px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0,
                        }}>
                            <BarChart3 size={22} color="white" />
                        </div>
                        <div style={{ minWidth: 0 }}>
                            <h2 style={{
                                color: 'white', fontSize: '18px', fontWeight: 700,
                                margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>
                                {drawerTitle}
                            </h2>
                            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px', margin: '2px 0 0' }}>
                                {context?.id ? 'Item Analytics' : context?.type === 'overview' ? 'Overview Analytics' : 'Page Analytics'}
                            </p>
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                            onClick={toggleFullscreen}
                            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                            style={{
                                padding: '8px', borderRadius: '8px', border: 'none',
                                backgroundColor: 'rgba(255,255,255,0.1)', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'background-color 0.2s',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                        >
                            {isFullscreen ? <Minimize2 size={20} color="white" /> : <Maximize2 size={20} color="white" />}
                        </button>
                        <button
                            onClick={onClose}
                            style={{
                                padding: '8px', borderRadius: '8px', border: 'none',
                                backgroundColor: 'rgba(255,255,255,0.1)', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'background-color 0.2s',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                        >
                            <X size={20} color="white" />
                        </button>
                    </div>
                </div>

                {/* ── CONTENT ────────────────────────── */}
                <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '20px 24px',
                    minWidth: 0,
                }}>
                    {loading ? (
                        <LoadingState />
                    ) : !data ? (
                        <EmptyState />
                    ) : (
                        <>
                            {/* Overview Stats Cards */}
                            {data.overview && <OverviewStats overview={data.overview} />}

                            {/* By Type Breakdown (overview mode) */}
                            {data.by_type && <TypeBreakdown items={data.by_type} />}

                            {/* Completion by Position */}
                            {data.by_position && data.by_position.length > 0 && (
                                <PositionBreakdown items={data.by_position} />
                            )}

                            {/* Top/Bottom Items (page/overview level) */}
                            {data.top_items && data.top_items.length > 0 && (
                                <ItemRanking title="Top Performing" items={data.top_items} variant="success" />
                            )}
                            {data.bottom_items && data.bottom_items.length > 0 && (
                                <ItemRanking title="Needs Attention" items={data.bottom_items} variant="warning" />
                            )}

                            {/* Employee Breakdown Table */}
                            {data.employees && data.employees.length > 0 && (
                                <EmployeeTable
                                    employees={data.employees}
                                    formatTime={formatTime}
                                    formatDate={formatDate}
                                    isAnonymous={data.is_anonymous}
                                />
                            )}

                            {/* AI Insights */}
                            {data.insights && data.insights.length > 0 && (
                                <InsightsSection insights={data.insights} />
                            )}

                            {/* AI Chat Panel */}
                            <AnalyticsAIPanel
                                analyticsContext={{
                                    type: context?.type || 'overview',
                                    id: context?.id,
                                    title: context?.title || drawerTitle,
                                    data: data,
                                }}
                            />

                            {/* Smart Notifications / Reminders */}
                            {context?.id && context?.type && (
                                <ActivityReminderSection
                                    activityType={context.type}
                                    activityId={context.id}
                                    activityTitle={context.title || drawerTitle}
                                />
                            )}
                        </>
                    )}
                </div>

                {/* ── FOOTER ─────────────────────────── */}
                {!loading && data && (
                    <div style={{
                        padding: '16px 24px',
                        borderTop: `1px solid ${TOKENS.border}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        flexShrink: 0,
                        backgroundColor: TOKENS.bgPage,
                    }}>
                        {context?.id && (
                            <button
                                onClick={handleExport}
                                disabled={exporting}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    padding: '10px 18px', borderRadius: TOKENS.cardRadius,
                                    border: `1px solid ${TOKENS.border}`,
                                    backgroundColor: TOKENS.bgCard,
                                    color: TOKENS.textPrimary,
                                    fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                                    opacity: exporting ? 0.6 : 1,
                                    transition: 'all 0.2s',
                                }}
                                onMouseEnter={(e) => { if (!exporting) e.currentTarget.style.backgroundColor = TOKENS.bgHover; }}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = TOKENS.bgCard}
                            >
                                <Download size={15} />
                                {exporting ? 'Exporting...' : 'Export CSV'}
                            </button>
                        )}
                        <div style={{ flex: 1 }} />
                        <button
                            onClick={onClose}
                            style={{
                                padding: '10px 20px', borderRadius: TOKENS.cardRadius,
                                border: 'none',
                                backgroundColor: TOKENS.navy,
                                color: 'white',
                                fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                                transition: 'background-color 0.2s',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = TOKENS.navyLight}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = TOKENS.navy}
                        >
                            Close
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};


// ═══════════════════════════════════════════════════════════
//  SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════

// ── Loading State ──
const LoadingState = () => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '300px', gap: '16px' }}>
        <div style={{
            width: '40px', height: '40px', border: `3px solid ${TOKENS.border}`,
            borderTopColor: TOKENS.accent, borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
        }} />
        <p style={{ color: TOKENS.textMuted, fontSize: '14px' }}>Loading analytics...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
);

// ── Empty State ──
const EmptyState = () => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '300px', gap: '12px' }}>
        <BarChart3 size={48} color={TOKENS.textMuted} />
        <p style={{ color: TOKENS.textSecondary, fontSize: '15px', fontWeight: 600 }}>No Data Available</p>
        <p style={{ color: TOKENS.textMuted, fontSize: '13px' }}>There isn't enough data to display analytics yet.</p>
    </div>
);

// ── Overview Stats Cards ──
const OverviewStats = ({ overview }) => {
    const cards = [
        {
            label: overview.label_completed ? 'Total' : 'Assigned',
            value: overview.total_assigned || 0,
            icon: Users, color: TOKENS.navy, bg: '#EFF2F5',
        },
        {
            label: overview.label_completed || 'Completed',
            value: overview.completed || overview.total_completed || 0,
            icon: CheckCircle, color: TOKENS.success, bg: TOKENS.successBg,
        },
    ];

    if (overview.overdue > 0 || overview.total_overdue > 0) {
        cards.push({
            label: 'Overdue',
            value: overview.overdue || overview.total_overdue || 0,
            icon: AlertTriangle, color: '#DC2626', bg: '#FEF2F2',
        });
    }

    if (overview.avg_completion_time) {
        cards.push({
            label: 'Avg Time',
            value: overview.avg_completion_time < 60
                ? `${Math.round(overview.avg_completion_time)}m`
                : `${Math.floor(overview.avg_completion_time / 60)}h ${Math.round(overview.avg_completion_time % 60)}m`,
            icon: Clock, color: TOKENS.accent, bg: TOKENS.accentBg,
            isText: true,
        });
    }

    return (
        <div style={{ marginBottom: '24px' }}>
            {/* Completion Rate Ring */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: '20px',
                padding: '20px', borderRadius: '10px',
                background: `linear-gradient(135deg, ${TOKENS.bgPage}, ${TOKENS.bgHover})`,
                marginBottom: '16px',
            }}>
                <CompletionRing
                    rate={overview.completion_rate || overview.overall_completion_rate || 0}
                />
                <div>
                    <div style={{ fontSize: '13px', color: TOKENS.textMuted, fontWeight: 500, marginBottom: '4px' }}>
                        Completion Rate
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: 800, color: TOKENS.textPrimary, lineHeight: 1 }}>
                        {overview.completion_rate || overview.overall_completion_rate || 0}%
                    </div>
                    {overview.trend !== undefined && overview.trend !== 0 && (
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: '4px', marginTop: '6px',
                            fontSize: '12px', fontWeight: 600,
                            color: overview.trend > 0 ? TOKENS.success : '#DC2626',
                        }}>
                            {overview.trend > 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                            {overview.trend > 0 ? '+' : ''}{overview.trend} vs last month
                        </div>
                    )}
                </div>
            </div>

            {/* Stat Cards Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(cards.length, 3)}, 1fr)`, gap: '10px' }}>
                {cards.map((card, i) => {
                    const Icon = card.icon;
                    return (
                        <div key={i} style={{
                            padding: '14px', borderRadius: '8px',
                            backgroundColor: card.bg, textAlign: 'center',
                        }}>
                            <Icon size={18} color={card.color} style={{ marginBottom: '6px' }} />
                            <div style={{ fontSize: card.isText ? '16px' : '22px', fontWeight: 700, color: card.color }}>
                                {card.value}
                            </div>
                            <div style={{ fontSize: '11px', color: TOKENS.textMuted, fontWeight: 500, marginTop: '2px' }}>
                                {card.label}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ── Completion Ring ──
const CompletionRing = ({ rate, size = 72 }) => {
    const strokeWidth = 6;
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (rate / 100) * circumference;
    const color = rate >= 75 ? TOKENS.success : rate >= 50 ? TOKENS.accent : '#DC2626';

    return (
        <svg width={size} height={size} style={{ flexShrink: 0 }}>
            <circle
                cx={size / 2} cy={size / 2} r={radius}
                fill="none" stroke={TOKENS.border} strokeWidth={strokeWidth}
            />
            <circle
                cx={size / 2} cy={size / 2} r={radius}
                fill="none" stroke={color} strokeWidth={strokeWidth}
                strokeDasharray={circumference} strokeDashoffset={offset}
                strokeLinecap="round"
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
                style={{ transition: 'stroke-dashoffset 0.8s ease-out' }}
            />
        </svg>
    );
};

// ── Type Breakdown (Overview mode) ──
const TypeBreakdown = ({ items }) => (
    <div style={{ marginBottom: '24px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: 700, color: TOKENS.textPrimary, margin: '0 0 12px' }}>
            By Activity Type
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {items.filter(i => i.total > 0).map((item, i) => (
                <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '12px', borderRadius: '8px',
                    border: `1px solid ${TOKENS.borderLight}`,
                }}>
                    <div style={{
                        width: '8px', height: '8px', borderRadius: '50%',
                        backgroundColor: item.color, flexShrink: 0,
                    }} />
                    <span style={{ fontSize: '13px', fontWeight: 600, color: TOKENS.textPrimary, flex: 1 }}>
                        {item.type}
                    </span>
                    <span style={{ fontSize: '12px', color: TOKENS.textMuted, marginRight: '8px' }}>
                        {item.total} items
                    </span>
                    <div style={{ width: '80px', height: '6px', backgroundColor: TOKENS.border, borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{
                            height: '100%', width: `${item.completion_rate}%`,
                            backgroundColor: item.color, borderRadius: '3px',
                            transition: 'width 0.6s ease-out',
                        }} />
                    </div>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: TOKENS.textPrimary, minWidth: '35px', textAlign: 'right' }}>
                        {item.completion_rate}%
                    </span>
                </div>
            ))}
        </div>
    </div>
);

// ── Position Breakdown ──
const PositionBreakdown = ({ items }) => (
    <div style={{ marginBottom: '24px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: 700, color: TOKENS.textPrimary, margin: '0 0 12px' }}>
            Completion by Role
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {items.slice(0, 8).map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{
                        fontSize: '12px', color: TOKENS.textSecondary,
                        width: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                        {item.position}
                    </span>
                    <div style={{
                        flex: 1, height: '8px', backgroundColor: TOKENS.bgHover,
                        borderRadius: '4px', overflow: 'hidden',
                    }}>
                        <div style={{
                            height: '100%', width: `${item.rate}%`,
                            backgroundColor: item.rate >= 75 ? TOKENS.success : item.rate >= 50 ? TOKENS.accent : '#DC2626',
                            borderRadius: '4px',
                            transition: 'width 0.6s ease-out',
                        }} />
                    </div>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: TOKENS.textPrimary, minWidth: '65px', textAlign: 'right' }}>
                        {item.completed}/{item.total} ({item.rate}%)
                    </span>
                </div>
            ))}
        </div>
    </div>
);

// ── Item Ranking ──
const ItemRanking = ({ title, items, variant }) => {
    const isSuccess = variant === 'success';
    return (
        <div style={{
            marginBottom: '24px', padding: '16px', borderRadius: '8px',
            backgroundColor: isSuccess ? TOKENS.successBg : TOKENS.warningBg,
            border: `1px solid ${isSuccess ? '#BBF7D0' : '#FDE68A'}`,
        }}>
            <h3 style={{
                fontSize: '13px', fontWeight: 700, margin: '0 0 10px',
                color: isSuccess ? TOKENS.successText : TOKENS.warningText,
            }}>
                {isSuccess ? '🏆' : '⚠️'} {title}
            </h3>
            {items.map((item, i) => (
                <div key={i} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '6px 0', borderBottom: i < items.length - 1 ? `1px solid ${isSuccess ? '#BBF7D0' : '#FDE68A'}` : 'none',
                }}>
                    <span style={{ fontSize: '13px', color: TOKENS.textPrimary, fontWeight: 500 }}>
                        {item.title}
                    </span>
                    <span style={{
                        fontSize: '12px', fontWeight: 700,
                        color: isSuccess ? TOKENS.successText : TOKENS.warningText,
                    }}>
                        {item.rate}%
                    </span>
                </div>
            ))}
        </div>
    );
};

// ── Employee Table ──
const EmployeeTable = ({ employees, formatTime, formatDate, isAnonymous }) => {
    const [showAll, setShowAll] = useState(false);
    const displayed = showAll ? employees : employees.slice(0, 10);

    if (isAnonymous) {
        return (
            <div style={{
                marginBottom: '24px', padding: '16px', borderRadius: '8px',
                backgroundColor: TOKENS.bgHover, textAlign: 'center',
            }}>
                <p style={{ color: TOKENS.textMuted, fontSize: '13px' }}>
                    This survey is anonymous — individual responses are hidden.
                </p>
            </div>
        );
    }

    const getStatusChip = (status) => {
        const styles = {
            completed: { bg: TOKENS.successBg, color: TOKENS.successText, label: 'Completed' },
            in_progress: { bg: '#EFF6FF', color: '#1D4ED8', label: 'In Progress' },
            pending: { bg: TOKENS.bgHover, color: TOKENS.textSecondary, label: 'Not Started' },
            overdue: { bg: '#FEF2F2', color: '#DC2626', label: 'Overdue' },
            pending_review: { bg: TOKENS.warningBg, color: TOKENS.warningText, label: 'Review' },
            active: { bg: '#EFF6FF', color: '#1D4ED8', label: 'Active' },
        };
        const s = styles[status] || styles.pending;
        return (
            <span style={{
                display: 'inline-block', padding: '2px 8px', borderRadius: '10px',
                fontSize: '11px', fontWeight: 600,
                backgroundColor: s.bg, color: s.color,
            }}>
                {s.label}
            </span>
        );
    };

    return (
        <div style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 700, color: TOKENS.textPrimary, margin: '0 0 12px' }}>
                Employee Breakdown
            </h3>
            <div style={{
                border: `1px solid ${TOKENS.borderLight}`, borderRadius: '8px', overflow: 'hidden',
            }}>
                {/* Table Header */}
                <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 120px 90px 80px',
                    padding: '10px 14px', backgroundColor: TOKENS.bgPage,
                    borderBottom: `1px solid ${TOKENS.borderLight}`,
                }}>
                    <span style={{ fontSize: '11px', fontWeight: 600, color: TOKENS.textMuted, textTransform: 'uppercase' }}>Employee</span>
                    <span style={{ fontSize: '11px', fontWeight: 600, color: TOKENS.textMuted, textTransform: 'uppercase' }}>Position</span>
                    <span style={{ fontSize: '11px', fontWeight: 600, color: TOKENS.textMuted, textTransform: 'uppercase' }}>Status</span>
                    <span style={{ fontSize: '11px', fontWeight: 600, color: TOKENS.textMuted, textTransform: 'uppercase', textAlign: 'right' }}>Date</span>
                </div>

                {/* Table Rows */}
                {displayed.map((emp, i) => (
                    <div
                        key={emp.id || i}
                        style={{
                            display: 'grid', gridTemplateColumns: '1fr 120px 90px 80px',
                            padding: '10px 14px', alignItems: 'center',
                            borderBottom: i < displayed.length - 1 ? `1px solid ${TOKENS.borderLight}` : 'none',
                            transition: 'background-color 0.15s',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = TOKENS.bgHover}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                        <div>
                            <div style={{ fontSize: '13px', fontWeight: 600, color: TOKENS.textPrimary }}>
                                {emp.first_name} {emp.last_name}
                            </div>
                            {emp.email && (
                                <div style={{ fontSize: '11px', color: TOKENS.textMuted }}>
                                    {emp.email}
                                </div>
                            )}
                        </div>
                        <span style={{ fontSize: '12px', color: TOKENS.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {emp.position || '—'}
                        </span>
                        {getStatusChip(emp.status)}
                        <span style={{ fontSize: '12px', color: TOKENS.textMuted, textAlign: 'right' }}>
                            {formatDate(emp.completed_at)}
                        </span>
                    </div>
                ))}

                {/* Show More */}
                {employees.length > 10 && (
                    <button
                        onClick={() => setShowAll(!showAll)}
                        style={{
                            width: '100%', padding: '10px',
                            border: 'none', borderTop: `1px solid ${TOKENS.borderLight}`,
                            backgroundColor: TOKENS.bgPage, cursor: 'pointer',
                            fontSize: '12px', fontWeight: 600, color: TOKENS.accent,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                        }}
                    >
                        {showAll ? 'Show Less' : `Show All ${employees.length} Employees`}
                        <ChevronRight size={14} style={{ transform: showAll ? 'rotate(-90deg)' : 'rotate(90deg)', transition: 'transform 0.2s' }} />
                    </button>
                )}
            </div>
        </div>
    );
};

// ── AI Insights ──
const InsightsSection = ({ insights }) => (
    <div style={{
        padding: '18px', borderRadius: '10px',
        backgroundColor: TOKENS.bgHover,
        marginBottom: '8px',
    }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
            <Lightbulb size={18} color={TOKENS.accent} />
            <h3 style={{ fontSize: '14px', fontWeight: 700, color: TOKENS.textPrimary, margin: 0 }}>
                AI Insights
            </h3>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {insights.map((insight, i) => {
                const iconMap = {
                    positive: { icon: ArrowUpRight, color: TOKENS.success },
                    warning: { icon: AlertTriangle, color: TOKENS.warning },
                    info: { icon: Minus, color: TOKENS.textMuted },
                };
                const { icon: Icon, color } = iconMap[insight.type] || iconMap.info;
                return (
                    <div key={i} style={{
                        display: 'flex', alignItems: 'flex-start', gap: '10px',
                        padding: '10px 12px', borderRadius: '6px',
                        backgroundColor: TOKENS.bgCard,
                        border: `1px solid ${TOKENS.borderLight}`,
                    }}>
                        <Icon size={16} color={color} style={{ marginTop: '1px', flexShrink: 0 }} />
                        <p style={{ fontSize: '13px', color: TOKENS.textSecondary, margin: 0, lineHeight: 1.5 }}>
                            {insight.text}
                        </p>
                    </div>
                );
            })}
        </div>
    </div>
);


export default AnalyticsDrawer;
